import React from 'react';

import { required } from '../../util/validators';
import { FieldRadioButton } from '../../components';

import css from './PublishingOptionsField.module.css';

const FIELD_KEY = 'publishing_options';

/**
 * Renders the publishing_options user field as a radio-button group where each
 * option has a short explanatory description below it.
 *
 * Strictly key-scoped: returns null unless fieldConfig.key === 'publishing_options'.
 * Does not modify FieldRadioButton or FieldSelect.
 *
 * @component
 * @param {Object} props
 * @param {string} props.name - Final Form field name (e.g. "priv_publishing_options")
 * @param {string} [props.formId] - Optional form id prefix
 * @param {Object} props.fieldConfig - The user field configuration from Console
 * @param {string} [props.label] - Group heading label (pass null to suppress)
 * @param {string} props.defaultRequiredMessage - Fallback required message
 * @param {Object} props.intl - react-intl instance
 */
const PublishingOptionsField = props => {
  const { name, formId, fieldConfig, label, defaultRequiredMessage, intl } = props;

  if (fieldConfig?.key !== FIELD_KEY) {
    return null;
  }

  const { enumOptions = [], saveConfig } = fieldConfig;
  const { isRequired, requiredMessage } = saveConfig || {};
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};

  return (
    <div className={css.field}>
      {label ? <legend className={css.groupLabel}>{label}</legend> : null}
      {enumOptions.map(o => {
        const optionId = formId ? `${formId}.${name}.${o.option}` : `${name}.${o.option}`;
        const descriptionId = `PublishingOptionsField.description.${o.option}`;
        const descriptionText = intl.messages?.[descriptionId]
          ? intl.formatMessage({ id: descriptionId })
          : null;

        return (
          <div key={o.option} className={css.option}>
            <FieldRadioButton
              id={optionId}
              name={name}
              value={o.option}
              label={o.label}
              {...validateMaybe}
            />
            {descriptionText ? (
              <p className={css.optionDescription}>{descriptionText}</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default PublishingOptionsField;
